import { Component, ChangeDetectionStrategy, computed, model, inject } from '@angular/core';
import { MatDialogRef, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';

export type SunTaxPopupComponentResponse = 'dont-show-again' | undefined;

@Component({
  selector: 'app-sun-tax-popup',
  template: `
    <mat-dialog-content>
      <h1>{{ taxYear() }} Tax Information</h1>
      <p class="bold">Find out how to access tax documents for tax year {{ taxYear() }}.</p>

      <hr />

      <div class="access-options">
        <div class="access-options__section">
          <div class="access-options__section--image">
            <img src="/media/static/app/browser/assets/sun/tax-popup/computer.png" />
          </div>
          <div>
            <h2>Accessing your tax forms online</h2>
            <p>Tax information will be available online by January 31, {{ nextYear() }}.</p>
          </div>
        </div>
      </div>
      <hr />
      <div class="access-options">
        <div class="access-options__section">
          <div class="access-options__section--image">
            <img src="/media/static/app/browser/assets/sun/tax-popup/envelope.png" />
          </div>
          <div>
            <h2>Receiving your tax forms by mail</h2>

            <p>We will mail your {{ taxYear() }} IRS Form 1098 no later than January 31, {{ nextYear() }}.</p>
          </div>
        </div>
      </div>

      <hr />

      <h1>Frequently Asked Questions</h1>

      <ol>
        <li>
          <h4>Can I view my mortgage tax forms online?</h4>
          <ul>
            <li>
              Tax forms are available online by January 31, {{ nextYear() }}. You can see, download and print tax forms by signing in to
              your MYL (MyLoanInfo) account.
            </li>
          </ul>
        </li>

        <li>
          <h4>Can I access previous tax forms?</h4>
          <ul>
            <li>
              You can access previous year tax forms by signing in to your MYL (MyLoanInfo) account. Select "IRS 1040" in the My Loan
              Section. Go to the "Tax Documents" section and choose the tax year to download the tax form.
            </li>
          </ul>
        </li>

        <li>
          <h4>Will I receive more than one Form 1098 for the same property in a year?</h4>
          <ul>
            <li>
              You should receive Form 1098 from each company to which you paid reportable mortgage interest or points. You could receive
              more than one Form 1098 if you refinanced the property during the year and/or if servicing on the loan was transferred.
            </li>
          </ul>
        </li>

        <li>
          <h4>How will I know when my tax forms are available online?</h4>
          <ul>
            <li>
              We will send you an email at your primary email address when your tax forms are available online. Please sign in to your MYL
              (MyLoanInfo) account to confirm we have your correct email address on file.
            </li>
          </ul>
        </li>

        <li>
          <h4>What should I do if I'm having trouble seeing or downloading my tax forms?</h4>
          <ul>
            <li>Try downloading the latest version of Adobe Reader or using a different browser.</li>
          </ul>
        </li>

        <li>
          <h4>Can I call the Customer Service Department to request that they expedite my tax forms?</h4>
          <ul>
            <li>
              No. Customer Service does not have the ability to expedite your tax forms. The fastest way for you to access your tax forms is
              to wait until they become available online.
            </li>
          </ul>
        </li>
      </ol>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button class="btn btn-default" [mat-dialog-close]="true" (click)="dontShowAgain($event)">Don't show again</button>
      <button class="btn btn-primary" [mat-dialog-close]="true">Ok</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .bold {
        font-weight: bold;
        text-align: center;
      }

      h1 {
        text-decoration: underline;
        text-align: center;
      }

      mat-dialog-actions button {
        margin-left: 10px;
      }

      .access-options {
        display: flex;
        align-items: center;
        flex-direction: column;

        &__section {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 80%;

          &--image {
            width: 150px;
          }

          @media (min-width: 600px) {
            flex-direction: row;
            gap: 20px;
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogContent, MatDialogActions, MatDialogClose],
})
export class SunTaxPopupComponent {
  private dialogRef = inject<MatDialogRef<SunTaxPopupComponent, SunTaxPopupComponentResponse>>(MatDialogRef);

  taxYear = model<number | undefined>();

  nextYear = computed(() => {
    const taxYear = this.taxYear();

    if (!taxYear) {
      return;
    }

    return taxYear + 1;
  });

  dontShowAgain(event: MouseEvent) {
    event.preventDefault();
    this.dialogRef.close('dont-show-again');
  }
}
